import { sectionRenderer } from '@theme/section-renderer';

/**
 * Keeps the cart page and cart drawer in sync when Smile.io applies a reward code.
 *
 * Smile's launcher redeems a reward by requesting `/discount/<code>` (XHR via axios) and then
 * reading `/cart.js`. It never touches the theme or dispatches a cart event, so Horizon's
 * `cart-items-component` doesn't know the discount changed and the totals stay stale.
 *
 * We watch for same-origin `/discount/<code>` requests (XHR and fetch) and, once one finishes,
 * re-render every `cart-items-component` section through the Section Rendering API.
 */

const DISCOUNT_PATH = /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?discount\/[^/]+\/?$/i;
const REFRESH_DELAY = 150;

/** @type {number | undefined} */
let refreshTimer;

/**
 * @param {string | URL | Request} input
 * @returns {boolean}
 */
function isDiscountRequest(input) {
  try {
    const raw = input instanceof Request ? input.url : String(input);
    const url = new URL(raw, window.location.href);
    return url.origin === window.location.origin && DISCOUNT_PATH.test(url.pathname);
  } catch {
    return false;
  }
}

function refreshCartSections() {
  clearTimeout(refreshTimer);
  refreshTimer = window.setTimeout(() => {
    for (const component of document.querySelectorAll('cart-items-component')) {
      if (!(component instanceof HTMLElement)) continue;
      const { sectionId } = component.dataset;
      if (!sectionId) continue;

      sectionRenderer
        .renderSection(sectionId, {
          cache: false,
          mode: component.dataset.drawer !== undefined ? 'hydration' : 'full',
        })
        .catch((error) => {
          if (error?.name !== 'AbortError') console.warn('[smile-cart-sync] Section refresh failed:', error);
        });
    }
  }, REFRESH_DELAY);
}

if (!window.__smileCartSync) {
  window.__smileCartSync = true;

  const originalOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    if (isDiscountRequest(url)) {
      this.addEventListener('loadend', refreshCartSections, { once: true });
    }
    return originalOpen.call(this, method, url, ...rest);
  };

  const originalFetch = window.fetch;
  window.fetch = function (input, init) {
    const promise = originalFetch.call(this, input, init);
    if (isDiscountRequest(input)) {
      promise.finally(refreshCartSections).catch(() => {});
    }
    return promise;
  };
}
